import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getOrWaitForPageSession } from '../../network/service';

const normalizeApiList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const appendIfExists = (params, key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    params.append(key, value);
  }
};

export const affiliateReportApi = createApi({
  reducerPath: 'affiliateReportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_SERVER_HOST,
    prepareHeaders: async (headers) => {
      const accessToken = localStorage.getItem('access-token');
      const pubId = localStorage.getItem('pub_id');
      const roleId = localStorage.getItem('role_id');

      if (accessToken) {
        headers.set('access-token', accessToken);
      }

      if (pubId) {
        headers.set('pubid', pubId);
      }

      if (roleId) {
        headers.set('role_id', roleId);
      }

      try {
        const token = await getOrWaitForPageSession();
        headers.set('x-page-session', token);
      } catch (e) {
        console.warn('Page session unavailable:', e.message);
      }

      return headers;
    }
  }),
  tagTypes: ['AffiliateReport'],
  endpoints: (builder) => ({
    getAffiliateReport: builder.query({
      query: (filter = {}) => {
        const params = new URLSearchParams();

        appendIfExists(params, 'start_date', filter.start_date);
        appendIfExists(params, 'end_date', filter.end_date);
        appendIfExists(params, 'type', filter.appType?.value || 'click');

        if (filter.isAffiliate) {
          params.append('affiliate_checked', '1');
          appendIfExists(params, 'affiliate_id', filter.affiliate?.id);
        }

        if (filter.isAsset) {
          params.append('network_checked', '1');
          appendIfExists(params, 'network_id', filter.asset?.id);
        }

        if (filter.isCoupon) {
          params.append('coupon_checked', '1');
          appendIfExists(params, 'coupon_id', filter.coupon?.id || filter.coupon_id);
        }

        if (filter.isDeviceType) {
          params.append('device_type_checked', '1');
          appendIfExists(params, 'device_type', filter.deviceType?.value);
        }

        if (filter.isSubscription) {
          appendIfExists(params, 'subscription_id', filter.subscription?.id);
        }

        if (filter?.filters?.includes('user')) {
          params.append('user_checked', '1');
        }

        if (filter?.filters?.includes('aggregate')) {
          params.append('aggregate_checked', '1');
        }

        if (filter?.filters?.includes('subscription')) {
          params.append('subscription_checked', '1');
        }

        return {
          url: `/report/affiliate?${params.toString()}`,
          headers: {
            channelid: filter.channel?.channel_id || ''
          }
        };
      },
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'LIST' }]
    }),

    getAffiliateDeviceTypeList: builder.query({
      query: (channelId) => ({
        url: '/app/appType',
        headers: {
          channelid: channelId || ''
        }
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'DEVICE_TYPES' }]
    }),

    getAffiliateList: builder.query({
      query: (channelId) => ({
        url: '/affiliate/list',
        headers: {
          channelid: channelId || ''
        }
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'AFFILIATES' }]
    }),

    getAffiliateAssetList: builder.query({
      query: (affiliateId) => ({
        url: '/network/list',
        params: {
          affiliate_id: affiliateId
        }
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'ASSETS' }]
    }),

    getAffiliateCouponList: builder.query({
      query: (channelId) => ({
        url: '/coupon/list',
        headers: {
          channelid: channelId || ''
        }
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'COUPONS' }]
    }),

    getAffiliateSubscriptionList: builder.query({
      query: () => ({
        url: '/subscription/list'
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'SUBSCRIPTIONS' }]
    }),

    getAffiliateChannelList: builder.query({
      query: (pubId) => ({
        url: `/channel/list/${pubId || localStorage.getItem('pub_id') || ''}`,
        headers: {
          pub_id: pubId || localStorage.getItem('pub_id') || ''
        }
      }),
      transformResponse: normalizeApiList,
      providesTags: [{ type: 'AffiliateReport', id: 'CHANNELS' }]
    })
  })
});

export const {
  useLazyGetAffiliateReportQuery,
  useGetAffiliateDeviceTypeListQuery,
  useGetAffiliateListQuery,
  useGetAffiliateAssetListQuery,
  useGetAffiliateCouponListQuery,
  useGetAffiliateSubscriptionListQuery,
  useGetAffiliateChannelListQuery
} = affiliateReportApi;
